import { isServer } from '@unifyapps/network/utils/fetch/utils';
import { isNative } from '@unifyapps/ui/utils/isNative';
import { extractPropertyPaths } from '../../dependency/utils/extractPropertyPaths';
import { isDynamicValue, stringToJS } from './dynamicBindings';
import type { EvalMode, EvalOptions } from './indirectEval';
import { indirectEvalExpression as defaultIndirectEvalExpression } from './indirectEval';
import { isNoCodeEntityId } from './isNoCodeEntityId';
// oxlint-disable-next-line no-cycle -- pre-existing cycle: recursiveUpdate → getComputedData → evaluateExpression
import { defaultUpdater, resolveHardenedPathExpression } from './recursiveUpdate';

/**
 * checks if the evaluation result is a non-renderable type (Function, Symbol, Promise)
 * that cannot be displayed on the UI. returns undefined for such types.
 */
export function sanitizeEvalResult(result: unknown): unknown {
  // functions are not serializable or renderable
  if (typeof result === 'function') return undefined;
  // symbols cannot be serialized and should not leak to UI
  if (typeof result === 'symbol') return undefined;
  // promises can't be rendered in UI
  if (result instanceof Promise) return undefined;
  return result;
}

// though this is not an entity, ig used in ua entity table
const OTHER_RESERVED_KEYWORDS = ['currentRow'];

export const isValidEntityId = (entityId: string) => {
  return isNoCodeEntityId(entityId) || OTHER_RESERVED_KEYWORDS.includes(entityId);
};

export const isEntityIdPresentInValue = (expression: string) => {
  const { result } = extractPropertyPaths(expression, isValidEntityId);
  return Boolean(Object.keys(result).length);
};

// The predicate that decides whether an evaluation result which is itself an expression gets
// evaluated again. `doNotEvaluateExpression` is its "never re-evaluate" member.
export type ShouldEvaluateResult = (result: string) => boolean;

export const doNotEvaluateExpression: ShouldEvaluateResult = (_: string) => {
  return false;
};

// The server nested-eval flag routes the predicate to `doNotEvaluateExpression` (see
// getShouldEvaluateResult); that exact identity is the signal every eval entry point uses to
// switch on the SSR first-order hardening (UA-003/UA-004). Centralised so the check can't drift or
// be misspelled across call sites — and note it is deliberately identity-based: a look-alike such
// as a fresh `() => false` is value-equal but NOT this predicate, so it does not (and must not)
// trip the hardening.
export const isServerHardenedEval = (shouldEvaluateResult: ShouldEvaluateResult): boolean =>
  shouldEvaluateResult === doNotEvaluateExpression && isServer() && !isNative();

export function evaluateResult<T>({
  context,
  resolvedStringToEvaluate,
  shouldEvaluateResult,
  indirectEvalExpression,
  evalMode,
  onError,
}: {
  context: object;
  resolvedStringToEvaluate: string;
  shouldEvaluateResult: (result: string) => boolean;
  indirectEvalExpression?: (
    script: string,
    globalContext: object,
    options?: EvalOptions,
  ) => unknown;
  evalMode?: EvalMode;
  // opt-in side-channel: default undefined keeps every existing caller byte-identical.
  // Pulse threads this to capture run-script errors the swallow catch would otherwise hide.
  onError?: (error: unknown) => void;
}): T | undefined {
  try {
    // Thread evalMode uniformly: an injected evaluator gets the same options as the
    // default one, so the mode can't be silently dropped on the injected path.
    const evaluate = indirectEvalExpression ?? defaultIndirectEvalExpression;
    const result = evaluate(resolvedStringToEvaluate, context, { evalMode });

    // if the result value is also a dynamic value, then evaluate it recursively
    if (typeof result === 'string' && shouldEvaluateResult(result)) {
      // The recursion keeps the caller's evalMode but deliberately NOT its custom
      // indirectEvalExpression: page functions inject a statement-style evaluator
      // (no `return` wrapper) that cannot evaluate a nested `{{...}}` expression.
      // Nested results have always re-evaluated through the default expression
      // evaluator — only the permission level must survive the hop.
      return oldEvaluateExpression({
        context,
        expression: result,
        shouldEvaluateResult,
        evalMode,
        onError,
      });
    }

    return sanitizeEvalResult(result) as T;
  } catch (e) {
    // a faulty onError sink must never break evaluation
    try {
      onError?.(e);
    } catch {
      /* noop */
    }
    // console.error(
    //   `Error in evaluating expression ${resolvedStringToEvaluate} with context`,
    //   context,
    //   e,
    // );
  }
}

export function optimisedEvaluateResult<T>({
  context,
  resolvedStringToEvaluate,
  shouldEvaluateResult,
  expressionEvaluator,
  onError,
}: {
  context: object;
  resolvedStringToEvaluate: string;
  shouldEvaluateResult: (result: string) => boolean;
  expressionEvaluator: (script: string) => unknown;
  // opt-in side-channel; see evaluateResult for the contract (default undefined → no-op)
  onError?: (error: unknown) => void;
}): T | undefined {
  try {
    const result = expressionEvaluator(resolvedStringToEvaluate);
    // if the result value is also a dynamic value, then evaluate it recursively
    if (typeof result === 'string' && shouldEvaluateResult(result)) {
      return optimisedEvaluateExpression({
        context,
        expression: result,
        shouldEvaluateResult,
        expressionEvaluator,
        onError,
      });
    }

    return sanitizeEvalResult(result) as T;
  } catch (e) {
    try {
      onError?.(e);
    } catch {
      /* noop */
    }
    // console.error(`Error in evaluating expression ${resolvedStringToEvaluate} with context`, context, e);
  }
}

/**
 *
 * @deprecated use optimisedEvaluateExpression instead
 */
function oldEvaluateExpression<T>({
  context,
  expression,
  shouldEvaluateResult = isDynamicValue,
  evalMode,
  onError,
}: {
  expression: string;
  context: object;
  // this flag is used to resolve the result of the expression if it is a dynamic value
  // it keeps on evaluating the expression until it is not a dynamic value anymore
  shouldEvaluateResult?: (result: string) => boolean;
  evalMode?: EvalMode;
  onError?: (error: unknown) => void;
}): T | undefined {
  if (!isDynamicValue(expression)) return expression as T;

  // UA-004: under the server nested-eval flag, a whole-string path is read, never compiled.
  if (isServerHardenedEval(shouldEvaluateResult)) {
    const hardened = resolveHardenedPathExpression(expression, context);
    if (hardened.handled) return hardened.value as T;
  }

  const toBeResolvedJS = stringToJS(expression);
  const resolvedStringToEvaluate = defaultUpdater(context, toBeResolvedJS, shouldEvaluateResult) as
    | string
    | undefined;

  if (resolvedStringToEvaluate === undefined) return undefined;

  return evaluateResult<T>({
    context,
    resolvedStringToEvaluate,
    shouldEvaluateResult,
    evalMode,
    onError,
  });
}

function optimisedEvaluateExpression<T>({
  context,
  expression,
  shouldEvaluateResult = isDynamicValue,
  expressionEvaluator,
  onError,
}: {
  expression: string;
  context: object;
  // this flag is used to resolve the result of the expression if it is a dynamic value
  // it keeps on evaluating the expression until it is not a dynamic value anymore
  shouldEvaluateResult?: (result: string) => boolean;
  expressionEvaluator: (script: string) => unknown;
  onError?: (error: unknown) => void;
}): T | undefined {
  if (!isDynamicValue(expression)) return expression as T;

  // UA-004: under the server nested-eval flag, a whole-string path is read, never compiled.
  if (isServerHardenedEval(shouldEvaluateResult)) {
    const hardened = resolveHardenedPathExpression(expression, context);
    if (hardened.handled) return hardened.value as T;
  }

  const toBeResolvedJS = stringToJS(expression);

  const resolvedStringToEvaluate = defaultUpdater(context, toBeResolvedJS, shouldEvaluateResult) as
    | string
    | undefined;

  if (resolvedStringToEvaluate === undefined) return undefined;

  return optimisedEvaluateResult<T>({
    context,
    resolvedStringToEvaluate,
    shouldEvaluateResult,
    expressionEvaluator,
    onError,
  });
}

export { oldEvaluateExpression, optimisedEvaluateExpression };
